import { ChangeDetectionStrategy, Component, Input, HostListener } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { UiDisplayNutrient } from "models/display-nutrient";
import { TranslatedString, untranslatedString } from "models/translated-string";
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { UserDb } from 'services/user-db.service';
import { NavController } from 'providers/nav-controller';
import { INutritionInfoPage } from 'pages/nutrition-info-page/nutrition-info-interface';
import { INFOODS, infoodImportanceWeights } from '@cf-shared/data-model/food';
import { Formatting, nutrientOneLetterLabel } from 'util/formatting';
import * as barGeometry from './nutrient-bar-geometry';
import { NutrientBarCard, NutrientDisplayMode, ProgressBarLayout } from './nutrient-bar-geometry';

// re-exported so the pages that set a card's display mode keep importing it from here
export { NutrientDisplayMode } from './nutrient-bar-geometry';

export type NutrientListState = 'inline-compact' | 'linear' | 'linear-card' | 'food-macros' | 'compact-macros';

/** What the bar row of the `compact-macros` card is showing. */
export enum NutrientBarStyle {
  /**
   * One bar spanning all five columns, split by how the food's calories divide
   * between its macros. Used when the card describes a single food, which has
   * no targets to measure against.
   */
  Distribution = 'distribution',
  /** One bar per column, each measured against that macro's own target. */
  Targets = 'targets',
}

/**
 * One column of the `compact-macros` card. The card always has five, in a fixed
 * order, whether or not the data has a nutrient for each -- an absent one keeps
 * its column so the other four stay aligned under their labels.
 */
interface CompactMacroColumn {
  nutrient: UiDisplayNutrient | null;
  /**
   * Drives the `nutrient-color` attribute the stylesheet colours from. It is
   * fixed per column rather than read from the nutrient, so an absent macro's
   * column still knows which colour it would have been.
   */
  colorKey: string;
}

@Component({
    selector: 'nutrient-list',
    templateUrl: 'nutrient-list.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class NutrientListComponent {
  protected LL = LL;
  @Input() nutrients: UiDisplayNutrient[] = [];
  @Input() expandable: boolean = true;
  /**
   * Whether the `compact-macros` card draws its own "Nutrients ›" link under the columns.
   * Off only where the host puts that link somewhere else -- the Meal screen has it in its
   * card title. Tapping the card still opens the nutrient list while `expandable` is on.
   */
  @Input() showNutrientsLink: boolean = true;
  @Input() state: NutrientListState = 'inline-compact';
  @Input() match_pc: number | undefined = undefined;
  @Input() forceShowTrackedNutrientsInHeader: boolean = false;
  @Input() displayMode: NutrientDisplayMode = NutrientDisplayMode.Logged;
  @Input() hideLabel: boolean = false; /** Hide the "Logged:" / "Targets:" label in inline-compact mode */
  @Input() loading: boolean = false; /** Show loading skeleton instead of nutrient values */
  @Input() useFullLabels: boolean = false; /** Use full nutrient names (Protein, Carbs) instead of abbreviations (Pro, Cho) in linear state */

  /**
   * What the bar row of the `compact-macros` card shows. Ignored by every other
   * state.
   *
   * It is an explicit input rather than something worked out from the data,
   * because the data cannot tell the two cases apart: a card whose macros
   * happen to have no targets looks exactly like a card that wants the
   * distribution bar, and those must draw differently.
   */
  @Input() barStyle: NutrientBarStyle = NutrientBarStyle.Targets;

  // Expose enums to template
  readonly NutrientDisplayMode = NutrientDisplayMode;
  readonly NutrientBarStyle = NutrientBarStyle;

  hasClientSmallScreen = hasClientSmallScreen;
  Math = Math; // Make Math available in template
  INFOODS = INFOODS; // Make INFOODS available in template
  isNaN = isNaN; // Make isNaN available in template

  constructor(private navCtrl: NavController, public userDb: UserDb) {
  }

  @HostListener('click', ['$event'])
  async clickEvent(event: PointerEvent) {
    if (!this.expandable) {
      return;
    }
    // only the two macro-card states open NutritionInfoPage on component click
    if (this.state === 'food-macros' || this.state === 'compact-macros') {
      event.stopPropagation();
      event.preventDefault();
      await this.navCtrl.pushAsync(INutritionInfoPage, { local: { nutrients: this.nutrients } });
    }
    // other states: do nothing (NutrientListPopover was removed)
  }

  displayNutrient(n: UiDisplayNutrient): boolean {
    // if we are not tracking values (but only showing targets), then don't show those nutrients that don't have a target
    if (!this.userDb.nutritionTracking && n.valueLimit === undefined) {
      return false;
    }

    if (n.displayInHeader) {
      return true;
    }

    // Show tracked nutrients in header on large screens if forceShowTrackedNutrientsInHeader is set
    return (
      n.tracked && this.forceShowTrackedNutrientsInHeader && !hasClientSmallScreen() &&
      n.valueLong && n.valueLong !== '--'
    );
  }

  getNutrientProgressPercent(n: UiDisplayNutrient): string {
    if (isNaN(n.progress_pc) || n.progress_pc == null) {
      return '0%';
    }

    return n.progress_pc.toFixed(0) + '%';
  }

  getNutrientColor(n: UiDisplayNutrient): string {
    switch (n.tag) {
      case INFOODS.Calories:
        return 'calories';
      case INFOODS.Protein:
        return 'protein';
      case INFOODS.Fat:
        return 'fat';
      case INFOODS.CarbNet:
        return 'carbs';
      case INFOODS.Cholesterol:
        return 'cholesterol';
      case INFOODS.Fiber:
        return 'fiber';
      default:
        return 'default';
    }
  }

  /**
   * The five columns of the `compact-macros` card, in their fixed order.
   *
   * Always five entries, even when the data has no nutrient for one of them:
   * the column stays, holding a blank value over its label, so the other four
   * do not shuffle sideways between one card and the next.
   *
   * Deliberately NOT built from `getTrackedNutrients()`, whose result is decided
   * by the data rather than fixed: it returns every `tracked` nutrient, in the
   * array's own order. Cholesterol is tracked, so it would take a column, and
   * the count and order would follow whatever the caller passed rather than
   * always being these five in this order -- so a macro would change column, or
   * lose one, as the scope changed. Holding the columns still sideways is what
   * this method is for. (The card's HEIGHT is a separate matter and does change
   * between scopes on purpose: the target row is dropped on the food scope.)
   *
   * Two things that are NOT the reason, both of which have been written here
   * wrongly before: that method's extra target-based filter is gated on the
   * `linear` state and would never run for this one; and a macro missing from
   * the food cannot drop a column either, because `FormatNutrients()` builds
   * from `buildDefaultDisplayNutrients()`, which emits an entry for every known
   * nutrient and leaves a missing figure blank.
   *
   * Cholesterol is left out because five columns is what fits the width.
   */
  getCompactMacroColumns(): CompactMacroColumn[] {
    return [
      { nutrient: this.getNutrientEnergy() ?? null, colorKey: 'calories' },
      { nutrient: this.getProteinNutrient() ?? null, colorKey: 'protein' },
      { nutrient: this.getCarbNutrient() ?? null, colorKey: 'carbs' },
      { nutrient: this.getFatNutrient() ?? null, colorKey: 'fat' },
      { nutrient: this.getFiberNutrient() ?? null, colorKey: 'fiber' },
    ];
  }

  /**
   * The number on the card's top row: what is logged in Total mode, what is
   * left in Left mode -- signed, so past the target reads as a negative.
   *
   * No unit: the label row underneath already says which macro it is, and at
   * five columns across a phone there is no room to repeat it.
   */
  getCompactMacroValue(nutrient: UiDisplayNutrient): string {
    return String(Math.round(this.getNutrientDisplayValue(nutrient)));
  }

  /**
   * The card's second row: the target this macro is measured against, with the
   * word that says what kind of target it is. Empty when there is none, which
   * is ordinary rather than exceptional -- carbs commonly have no per-meal
   * target.
   *
   * Shown in BOTH display modes, which is what holds the card at one height
   * when the mode dial is switched.
   */
  getCompactMacroTarget(nutrient: UiDisplayNutrient): string {
    const numberText = barGeometry.getNutrientTargetNumberText(nutrient);
    if (numberText === '') {
      return '';
    }

    return `/${numberText}${barGeometry.getNutrientTargetKindSuffix(nutrient)}`;
  }

  // for the template; the rule and its reasons are in nutrient-bar-geometry.ts
  isOveragePastFloor(nutrient: UiDisplayNutrient): boolean {
    return barGeometry.isOveragePastFloor(nutrient);
  }

  getNutrientEnergy() {
    if (!this.nutrients || this.nutrients.length === 0) {
      return null;
    }
    return this.nutrients.find((n) => n.tag === INFOODS.Calories);
  }

  getMacroNutrientsExEnergy(): UiDisplayNutrient[] {
    if (!this.nutrients || this.nutrients.length === 0) {
      return [];
    }
    return this.nutrients.filter((n) => n.tag === INFOODS.Protein || n.tag === INFOODS.Fat || n.tag === INFOODS.CarbNet || n.tag === INFOODS.Fiber);
  }

  getTrackedNutrients(): UiDisplayNutrient[] {
    if (!this.nutrients || this.nutrients.length === 0) {
      return [];
    }
    let nutrients = this.nutrients.filter((n) => n.tracked);

    // In linear mode, filter out nutrients without any target/min/max
    if (this.state === 'linear') {
      nutrients = nutrients.filter((n) => {
        if (!n.target) return false;
        // Has target if any of these are defined
        return (n.target.target !== undefined && n.target.target !== null) ||
          (n.target.min !== undefined && n.target.min !== null) ||
          (n.target.max !== undefined && n.target.max !== null);
      });
    }
    return nutrients;
  }

  getTrackedNutrientsExEnergy(): UiDisplayNutrient[] {
    if (!this.nutrients || this.nutrients.length === 0) {
      return [];
    }
    // filter tracked nutrients excluding energy/calories
    let nutrients = this.nutrients.filter((n) => n.tracked && n.tag !== INFOODS.Calories);

    // filter out nutrients without any target/min/max (consistent with linear-card display expectations)
    nutrients = nutrients.filter((n) => {
      if (!n.target) return false;
      // has target if any of these are defined
      return (n.target.target !== undefined && n.target.target !== null) ||
        (n.target.min !== undefined && n.target.min !== null) ||
        (n.target.max !== undefined && n.target.max !== null);
    });

    return nutrients;
  }

  getUntrackedNutrients(): UiDisplayNutrient[] {
    if (!this.nutrients || this.nutrients.length === 0) {
      return [];
    }
    return this.nutrients.filter((n) => n.value && n.valueShort !== '0' && !n.tracked).sort((a, b) => { return a.name.localeCompare(b.name); });
  }

  getDisplayInHeaderNutrients(): UiDisplayNutrient[] {
    if (!this.nutrients || this.nutrients.length === 0) {
      return [];
    }
    let nutrients = this.nutrients.filter((n) => n.displayInHeader);

    // In linear mode, filter out nutrients without any target/min/max
    if (this.state === 'linear') {
      nutrients = nutrients.filter((n) => {
        if (!n.target) return false;
        // Has target if any of these are defined
        return (n.target.target !== undefined && n.target.target !== null) ||
               (n.target.min !== undefined && n.target.min !== null) ||
               (n.target.max !== undefined && n.target.max !== null);
      });
    }

    return nutrients;
  }

  isNutrientCompleted(n: UiDisplayNutrient): boolean {
    return n.progress_pc >= 100;
  }

  getNutrientShortName(nutrient: UiDisplayNutrient): TranslatedString {
    if (this.hasClientSmallScreen()) {
      if (nutrient.shortName && nutrient.displayInHeader) {
        // the first letter of the already-translated name -- it follows the language on its own
        return untranslatedString(nutrient.name[0]);
      }
      return untranslatedString('');
    }

    return nutrient.shortName;
  }

  getInlineCompactLabel(): string {
    // determine if we're showing logged values or just targets
    if (!this.userDb.nutritionTracking || !this.hasNutrientValues()) {
      const label = LL.nutrientList.targets();
      return this.hasClientSmallScreen() ? label : `${label}:`;
    }
    const label = LL.nutrientList.logged();
    return this.hasClientSmallScreen() ? label : `${label}:`;
  }

  hasNutrientValues(): boolean {
    if (!this.nutrients || this.nutrients.length === 0) {
      return false;
    }
    // Check if any nutrient has an actual value (not just target)
    return this.nutrients.some(n => n.value != null && n.value > 0);
  }

  // The macro labels come from the catalog, so each language abbreviates its OWN word rather than
  // the English one -- French fat is `L` (lipides), not `F`. Calories deliberately have no label:
  // the flame icon carries that column.
  getInlineCompactNutrientLabel(nutrient: UiDisplayNutrient): TranslatedString {
    if (nutrient.tag === INFOODS.Calories) {
      return untranslatedString('');
    }
    // a truncation of the already-translated name is the fallback, so it follows the language too
    return nutrientOneLetterLabel(nutrient.tag)
      ?? nutrient.shortName
      ?? untranslatedString(nutrient.name.substring(0, 3));
  }

  getLinearNutrientLabel(nutrient: UiDisplayNutrient): TranslatedString {
    // if useFullLabels is set, return full names
    if (this.useFullLabels) {
      return this.getNutrientCardLabel(nutrient);
    }
    switch (nutrient.tag) {
      case INFOODS.Protein:
        return LL.nutrientList.short.protein();
      case INFOODS.CarbNet:
        return LL.nutrientList.short.carbs();
      case INFOODS.Fat:
        return LL.nutrientList.short.fat();
      case INFOODS.Fiber:
        return LL.nutrientList.short.fiber();
      default:
        // a truncation of the translated name, so it follows the language already
        return nutrient.shortName || untranslatedString(nutrient.name.substring(0, 3));
    }
  }

  getNutrientCardLabel(nutrient: UiDisplayNutrient): TranslatedString {
    switch (nutrient.tag) {
      case INFOODS.Protein:
        return LL.nutrientList.protein();
      case INFOODS.CarbNet:
        return LL.nutrientList.carbs();
      case INFOODS.Fat:
        return LL.nutrientList.fat();
      case INFOODS.Fiber:
        return LL.nutrientList.fiber();
      case INFOODS.Cholesterol:
        // cholesterol reaches the macro card too (#4317). Without this case it
        // fell through to the `default` below, which is the English
        // food-database name and therefore never translated.
        return LL.nutrientList.cholesterol();
      default:
        // Every nutrient name is now catalog text (#4368), so this fallback is translated too.
        // The explicit cases above are still worth keeping: the macro card deliberately says
        // "Carbs", not the catalog's full "Carbohydrates".
        return nutrient.name;
    }
  }

  // for the template; the rule and its reasons are in nutrient-bar-geometry.ts
  getNutrientTargetValue(nutrient: UiDisplayNutrient): number | null {
    return barGeometry.getNutrientTargetValue(nutrient);
  }

  // for the template; the rule and its reasons are in nutrient-bar-geometry.ts
  getNutrientTargetSlashText(nutrient: UiDisplayNutrient): string {
    return barGeometry.getNutrientTargetSlashText(nutrient);
  }

  // Get progress percentage for circular charts (simple cap at 100%, no tickmark scaling)
  getCircularProgressPercentage(nutrient: UiDisplayNutrient): number {
    const value = Number(nutrient.value) || 0;
    const limit = this.getNutrientTargetValue(nutrient);

    if (!limit || limit <= 0) {
      return 0;
    }

    return Math.min((value / limit) * 100, 100);
  }

  // for the template; the rule and its reasons are in nutrient-bar-geometry.ts
  getNutrientDisplayValue(nutrient: UiDisplayNutrient): number {
    return barGeometry.getNutrientDisplayValue(nutrient, this.getBarCard());
  }

  getLinearNutrientValueString(nutrient: UiDisplayNutrient): string {
    // Generate the complete value string with proper formatting
    const targetNumberText = barGeometry.getNutrientTargetNumberText(nutrient);
    const unit = nutrient.valueUnit || '';
    const suffix = this.getNutrientMinMaxSuffix(nutrient);

    if (this.displayMode === NutrientDisplayMode.Remaining) {
      const remainingValue = Math.round(this.getNutrientDisplayValue(nutrient));
      // In remaining mode, just show remaining value with unit and suffix
      return unit ? `${remainingValue}${unit}${suffix}` : `${remainingValue}${suffix}`;
    } else {
      const currentValue = Math.round(nutrient.value || 0);
      if (targetNumberText !== '') {
        // Format as "current/targetunit suffix" with thin space before unit
        return `${currentValue}/${targetNumberText}${Formatting.thinSpace}${unit}${suffix}`.trim();
      } else {
        // Format as "currentunit suffix" with thin space when no target
        return unit ? `${currentValue}${Formatting.thinSpace}${unit}${suffix}` : `${currentValue}${suffix}`;
      }
    }
  }

  // for the template; the rule and its reasons are in nutrient-bar-geometry.ts
  isNutrientOverTarget(nutrient: UiDisplayNutrient): boolean {
    return barGeometry.isNutrientOverTarget(nutrient);
  }

  // for the template; the rule and its reasons are in nutrient-bar-geometry.ts
  getNutrientOverageAmount(nutrient: UiDisplayNutrient): number {
    return barGeometry.getNutrientOverageAmount(nutrient);
  }

  // for the template; the rule and its reasons are in nutrient-bar-geometry.ts
  getNutrientOveragePercentage(nutrient: UiDisplayNutrient): number {
    return barGeometry.getNutrientOveragePercentage(nutrient);
  }

  /**
   * The nutrients whose bars the shared scale factor is measured over: the ones
   * this state actually draws.
   *
   * It matters only for `compact-macros`, which is the one state that shows a
   * SUBSET of what it is given -- five named macros, cholesterol deliberately
   * left out. Cholesterol carries a floor of its own, so measuring over the
   * whole list would let a nutrient with no column on the card shrink every bar
   * that does have one, with nothing on screen to explain it.
   *
   * Every other state measures over the whole list, unchanged.
   */
  private getScaledNutrients(): UiDisplayNutrient[] {
    if (this.state === 'compact-macros') {
      return this.getCompactMacroColumns()
        .map(column => column.nutrient)
        .filter((nutrient): nutrient is UiDisplayNutrient => nutrient !== null);
    }
    return this.nutrients || [];
  }

  /**
   * What the bar arithmetic in `nutrient-bar-geometry.ts` needs to know about
   * this card: its display mode, and the nutrients whose bars it draws.
   * See design-docs/nutrient-bars.md.
   */
  private getBarCard(): NutrientBarCard {
    return { displayMode: this.displayMode, drawnNutrients: this.getScaledNutrients() };
  }

  // Get min/max suffix text for value display
  getNutrientMinMaxSuffix(nutrient: UiDisplayNutrient): string {
    // Don't show min/max suffix in Remaining mode
    if (this.displayMode === NutrientDisplayMode.Remaining) {
      return '';
    }

    // everything else -- no target at all, and a fixed target needing no word --
    // is decided by the delegate, which is where those rules now live
    return barGeometry.getNutrientTargetKindSuffix(nutrient);
  }

  // Check if nutrient satisfies its target (for green checkmark)
  isNutrientTargetSatisfied(nutrient: UiDisplayNutrient): boolean {
    if (!nutrient.target) return false;

    const current = Number(nutrient.value) || 0;

    // Has fixed target: check if close enough (within reasonable tolerance)
    if (nutrient.target.target !== undefined && nutrient.target.target !== null) {
      const target = Number(nutrient.target.target);
      // Consider satisfied if within 5% of target (or exactly on target)
      const tolerance = target * 0.05;
      return Math.abs(current - target) <= tolerance;
    }

    // Has min/max: check if within bounds
    const hasMin = nutrient.target.min !== undefined && nutrient.target.min !== null;
    const hasMax = nutrient.target.max !== undefined && nutrient.target.max !== null;

    if (hasMin && hasMax) {
      const min = Number(nutrient.target.min);
      const max = Number(nutrient.target.max);
      return current >= min && current <= max;
    }

    if (hasMin) {
      const min = Number(nutrient.target.min);
      return current >= min;
    }

    if (hasMax) {
      const max = Number(nutrient.target.max);
      return current <= max;
    }

    return false;
  }

  // Check if overall meal/day is OnTarget (≥95% accuracy)
  isOnTarget(): boolean {
    // only use match_pc if provided, otherwise return false
    if (this.match_pc !== undefined) {
      return this.match_pc >= 95;
    }
    return false;
  }

  // Determine if nutrient bar/circle should be colored green
  // Green coloring only when meal/day is OnTarget (≥95% accuracy)
  shouldShowGreenColor(): boolean {
    return this.isOnTarget();
  }

  // for the template; the rule and its reasons are in nutrient-bar-geometry.ts
  getProgressBarLayout(nutrient: UiDisplayNutrient): ProgressBarLayout {
    return barGeometry.getProgressBarLayout(nutrient, this.getBarCard());
  }

  getProteinNutrient(): UiDisplayNutrient | undefined {
    if (!this.nutrients) return undefined;
    return this.nutrients.find(n => n.tag === INFOODS.Protein);
  }

  getCarbNutrient(): UiDisplayNutrient | undefined {
    if (!this.nutrients) return undefined;
    return this.nutrients.find(n => n.tag === INFOODS.CarbNet);
  }

  getFatNutrient(): UiDisplayNutrient | undefined {
    if (!this.nutrients) return undefined;
    return this.nutrients.find(n => n.tag === INFOODS.Fat);
  }

  getFiberNutrient(): UiDisplayNutrient | undefined {
    if (!this.nutrients) return undefined;
    return this.nutrients.find(n => n.tag === INFOODS.Fiber);
  }

  /**
   * Calculate segments for the combined macro progress bar in food-macros state.
   * Returns an array of segments with their position, width, and color.
   * Segments are normalized to fill the full bar (100%).
   */
  getFoodMacrosBarSegments(): { left_pct: number, width_pct: number, color: string, roundLeft: boolean, roundRight: boolean }[] {
    if (!this.nutrients || this.nutrients.length === 0) {
      return [];
    }

    const energyNutrient = this.getNutrientEnergy();
    if (!energyNutrient || !energyNutrient.value || energyNutrient.value <= 0) {
      return [];
    }

    // Order: Protein, Carbs, Fat, Fiber (matching the display order)
    const macroOrder = [INFOODS.Protein, INFOODS.CarbNet, INFOODS.Fat, INFOODS.Fiber];

    // First pass: calculate raw calorie contributions
    const rawSegments: { calories: number, color: string }[] = [];
    let totalMacroCalories = 0;

    for (const tag of macroOrder) {
      const nutrient = this.nutrients.find(n => n.tag === tag);
      if (!nutrient || !nutrient.value || nutrient.value <= 0) {
        continue;
      }

      const caloriesPerGram = infoodImportanceWeights[tag] || 0;
      const nutrientCalories = caloriesPerGram * nutrient.value;

      // Only include if it contributes meaningful calories
      if (nutrientCalories >= 1) {
        rawSegments.push({
          calories: nutrientCalories,
          color: this.getNutrientColor(nutrient)
        });
        totalMacroCalories += nutrientCalories;
      }
    }

    if (rawSegments.length === 0 || totalMacroCalories <= 0) {
      return [];
    }

    // Second pass: normalize to 100% and build final segments
    const segments: { left_pct: number, width_pct: number, color: string, roundLeft: boolean, roundRight: boolean }[] = [];
    let currentPosition = 0;

    for (let i = 0; i < rawSegments.length; i++) {
      const raw = rawSegments[i];
      // Normalize: scale each segment so total fills 100%
      const widthPct = (raw.calories / totalMacroCalories) * 100;

      segments.push({
        left_pct: currentPosition,
        width_pct: widthPct,
        color: raw.color,
        roundLeft: i === 0,
        roundRight: i === rawSegments.length - 1
      });
      currentPosition += widthPct;
    }

    return segments;
  }

  getInlineCompactNutrients(): { protein?: UiDisplayNutrient, carb?: UiDisplayNutrient, fat?: UiDisplayNutrient, fiber?: UiDisplayNutrient } {
    return {
      protein: this.getProteinNutrient(),
      carb: this.getCarbNutrient(),
      fat: this.getFatNutrient(),
      fiber: this.getFiberNutrient()
    };
  }

  async handleNutritionInfoClick(event: Event) {
    event.stopImmediatePropagation();
    event.preventDefault();
    await this.navCtrl.pushAsync(INutritionInfoPage, { local: { nutrients: this.nutrients } });
  }

  trackByNutrientTag(_index: number, nutrient: UiDisplayNutrient): string {
    return nutrient.tag;
  }
}
